import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { PageHeader } from "../components/Surface";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { listAudit, type AuditEntry } from "../lib/admin";

export function AdminAudit() {
  const { roles } = useAuth();
  const isAdmin = can(roles, "admin_users");
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    listAudit(filter || undefined)
      .then((r) => { setEntries(r.entries); setActions(r.actions); })
      .finally(() => setLoading(false));
  }, [filter, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Audit log" subtitle="Admin access required." />
        <p className="font-mono text-sm text-danger">Admins only.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Audit log" subtitle="Every sensitive staff action, most recent first." />

      <label className="grid max-w-xs gap-1 text-xs font-bold text-muted">
        Filter by action
        <select className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by action">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="font-mono text-sm text-muted">No audit entries.</p>
      ) : (
        <div className="overflow-x-auto rounded-card border border-line bg-surface">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[150px_1fr_160px_1fr] items-center gap-4 border-b border-line px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-muted">
              <span>When</span><span>Actor</span><span>Action</span><span>Target / details</span>
            </div>
            {entries.map((e) => (
              <div key={e.id} className="grid grid-cols-[150px_1fr_160px_1fr] items-start gap-4 border-t border-line px-5 py-3">
                <span className="font-mono text-xs text-muted">{new Date(e.created_at).toLocaleString()}</span>
                <span className="truncate font-mono text-xs">{e.actor_email ?? (e.actor ? e.actor.slice(0, 8) : "system")}</span>
                <span className="font-mono text-xs font-bold text-accent-strong">{e.action}</span>
                <span className="min-w-0 font-mono text-[11px] text-muted">
                  {e.target ? <span className="text-ink">{e.target}</span> : null}
                  {e.metadata && Object.keys(e.metadata).length > 0 && <span className="block truncate">{JSON.stringify(e.metadata)}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="font-mono text-[11px] text-muted">Showing the {entries.length} most recent{filter ? ` ${labelize(filter)}` : ""} entries.</p>
    </div>
  );
}
